// InventarioListXL - Sprint 44 Fase 3 Vista 5.
// Lista de inventario valorizado XL. Reemplaza modules/inventario/list/.
// Header con titulo + valorizacion total destacada. Filtros (con_stock +
// busqueda local). XLTable con thumbnail + 8 columnas. Click fila → kardex.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useInventarioXL from './hooks/useInventarioXL';
import XLTable from '../../components/xl/XLTable';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import { fmtMoney } from '../../utils/money';
import { mediaUrl, fmtNum } from './utils';

const CSS_ID = 'xl-inv-list-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-inv-totalcard {
  background: rgba(5,150,105,0.08);
  border: 1px solid rgba(5,150,105,0.28);
  border-radius: var(--xl-radius-lg);
  padding: 12px 16px;
  min-width: 220px;
}
[data-xl-theme="dark"] .xl-inv-totalcard {
  background: rgba(16,185,129,0.14);
  border-color: rgba(16,185,129,0.32);
}
.xl-inv-totalcard__label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-success); font-weight: 700;
}
.xl-inv-totalcard__value {
  font-family: var(--xl-font-mono); font-size: 18px; font-weight: 600;
  color: var(--xl-color-success); margin-top: 2px;
}

.xl-filters {
  display: grid; grid-template-columns: 1fr 220px; gap: 12px;
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); padding: 16px; margin-bottom: 16px;
  box-shadow: var(--xl-shadow-sm);
}
@media (max-width: 720px) { .xl-filters { grid-template-columns: 1fr } }

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
  background: rgba(220,38,38,0.10); color: var(--xl-color-danger);
  border: 1px solid rgba(220,38,38,0.28);
}

.xl-inv-thumb {
  width: 36px; height: 36px;
  background: var(--xl-color-surface-alt);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-sm);
  display: inline-flex; align-items: center; justify-content: center;
  overflow: hidden;
}
.xl-inv-thumb img { max-width: 100%; max-height: 100%; object-fit: contain }
.xl-inv-thumb__empty { font-size: 10px; color: var(--xl-color-text-muted) }

.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-mono--strong { font-family: var(--xl-font-mono); font-weight: 600; color: var(--xl-color-success) }

.xl-link {
  color: var(--xl-color-info);
  font-size: 12px;
  font-weight: 500;
  background: transparent; border: none; cursor: pointer;
  padding: 6px 10px; border-radius: var(--xl-radius-sm);
}
.xl-link:hover { background: rgba(37,99,235,0.08) }
.xl-link:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
[data-xl-theme="dark"] .xl-link:hover { background: rgba(96,165,250,0.14) }
`;

function useDebouncedSetter(setter, delay) {
  const [local, setLocal] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setter(local), delay);
    return () => clearTimeout(t);
  }, [local, delay, setter]);
  return [local, setLocal];
}

const FILTRO_STOCK = [
  { value: '',     label: 'Todos los productos' },
  { value: 'true', label: 'Con existencias' },
  { value: 'false', label: 'Sin existencias' },
];

export default function InventarioListXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const navigate = useNavigate();
  const ix = useInventarioXL();
  const [searchLocal, setSearchLocal] = useDebouncedSetter(ix.setQ, 200);

  const columns = useMemo(() => ([
    { key: '_thumb', label: '',
      render: (r) => (
        <span className="xl-inv-thumb" aria-hidden="true">
          {r.imagen_thumbnail_url
            ? <img src={mediaUrl(r.imagen_thumbnail_url)} alt="" />
            : <span className="xl-inv-thumb__empty">\u2014</span>}
        </span>
      ) },
    { key: 'codigo', label: 'Codigo',
      render: (r) => <span className="xl-mono">{r.codigo}</span> },
    { key: 'nombre', label: 'Nombre' },
    { key: 'categoria', label: 'Categoria',
      render: (r) => r.categoria || '\u2014' },
    { key: 'existencias', label: 'Existencias', align: 'right',
      render: (r) => <span className="xl-mono">{fmtNum(r.existencias, 2)}</span> },
    { key: 'costo_promedio', label: 'Costo prom.', align: 'right',
      render: (r) => <span className="xl-mono">{fmtNum(r.costo_promedio)}</span> },
    { key: 'costo_ultimo', label: 'Costo ultimo', align: 'right',
      render: (r) => <span className="xl-mono">{fmtNum(r.costo_ultimo)}</span> },
    { key: 'valorizacion', label: 'Valorizacion', align: 'right',
      render: (r) => (
        <span className="xl-mono--strong">{fmtNum(r.valorizacion, 0)}</span>
      ) },
  ]), []);

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Inventario valorizado</h2>
          <p className="xl-page__subtitle">
            {ix.total} productos en catalogo. Click en una fila para ver el kardex.
          </p>
        </div>
        <div className="xl-inv-totalcard">
          <div className="xl-inv-totalcard__label">Valorizacion total</div>
          <div className="xl-inv-totalcard__value">
            {fmtMoney(ix.valorizacionTotal, 'COP')}
          </div>
        </div>
      </header>

      <section className="xl-filters" aria-label="Filtros de inventario">
        <XLInput
          type="search"
          label="Buscar"
          placeholder="Codigo, nombre o categoria..."
          value={searchLocal}
          onChange={(e) => setSearchLocal(e.target.value)}
        />
        <XLSelect
          label="Filtrar"
          value={ix.conStock}
          onChange={(e) => ix.setConStock(e.target.value)}
          options={FILTRO_STOCK}
        />
      </section>

      {ix.err && <div className="xl-banner" role="alert">{ix.err}</div>}

      <XLTable
        ariaLabel="Inventario valorizado"
        columns={columns}
        data={ix.items}
        loading={ix.loading}
        onRowClick={(r) => navigate('/inventario/' + r.id + '/kardex')}
        rowKey="id"
        emptyMessage="Sin productos para estos filtros."
      />
    </div>
  );
}
